import { FC, useState } from "react";

import styles from "./UploadedTablesSection.module.css";

import type { SessionWithUser } from "src/utils/users/types";
import type { ActiveContext } from "shared/ContextSwitcher/store";
import { pick } from "lodash";
import { useUploadedTablesQuery } from "src/gql/codegen/graphql";

import { UploadedTablesList } from "./UploadedTablesList";
import { Loading } from "src/components/loading/loading";
import { ContainedMessage } from "src/components/ui-components/SectionMessage/ContainedMessage";
import { Stairs003 } from "src/components/ui-components/Illustrations/Stairs003";
import { Notification } from "src/components/ui-components/Notification";
import { IconArrowRight } from "src/components/ui-components/Icons/IconArrowRight";
import { Button } from "src/components/ui-components/Button/Button";
import { ModalWithoutTrigger } from "src/components/ui-components/Modal";
import { DataUploadModalContent } from "shared/DataUpload/DataUploadModalContent";
import { ButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/ButtonTextOnly";

interface UploadedTablesSectionProps {
  session: SessionWithUser;
  activeContext: ActiveContext;
  canEdit: boolean;
}

export const UploadedTablesSection: FC<UploadedTablesSectionProps> = (
  props
) => {
  const { session, activeContext, canEdit } = props;
  const [isDataUploadModalOpen, setIsDataUploadModalOpen] = useState(false);

  const { data, loading } = useUploadedTablesQuery({
    context: { session },
    variables: {
      context_owner: pick(activeContext, ["id", "type"]),
    },
    fetchPolicy: "cache-first",
  });

  const uploadedTables = data?.uploaded_tables ?? [];

  if (loading) {
    return (
      <section className={styles.loadingContainer}>
        <Loading />
      </section>
    );
  }

  return (
    <>
      {uploadedTables.length === 0 ? (
        <div className={styles.emptyState}>
          <ContainedMessage
            heading="No table submissions yet!"
            body="When you submit tables they will show up here."
            image={<Stairs003 />}
            actions={
              <Button
                theme="primary"
                size="M"
                onClick={() => setIsDataUploadModalOpen(true)}
              >
                Upload new table
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <section>
            <UploadedTablesList
              uploadedTables={uploadedTables}
              canEdit={canEdit}
            />
          </section>
          <section>
            <Notification
              level="info"
              title="Bring your data into Dune"
              dismissable={false}
              actions={
                <ButtonTextOnly
                  size="M"
                  theme="primary"
                  onClick={() => setIsDataUploadModalOpen(true)}
                >
                  Upload data table <IconArrowRight />
                </ButtonTextOnly>
              }
            />
          </section>
        </>
      )}
      <ModalWithoutTrigger
        label="Upload a dataset"
        size="M"
        content={
          <DataUploadModalContent
            onDismiss={() => setIsDataUploadModalOpen(false)}
          />
        }
        isOpen={isDataUploadModalOpen}
        onDismiss={undefined}
      />
    </>
  );
};
